<template>
  <div class="bf-button focus:outline-none" tabindex="-1" :class="executingClass">
    <skeleton-loader v-if="loading"
                     shimmer-color="#ffffff"
                     :bg-class="skeletonClass"
                     class="text-sm block w-full rounded-full"
                     :class="[sizeClasses]"
                     style="min-width: 13rem;"/>

    <button v-else
            ref="buttonRef"
            :type="type"
            :tabindex="tabindex"
            class="w-full flex items-center justify-center gap-2 transition-colors whitespace-nowrap focus:!ring-0 focus:!ring-offset-0 focus-visible:!outline-none focus-visible:!ring-0 relative select-none"
            :class="[sizeClasses, colorClasses, executingClass, classString, selectedClasses]"
            :disabled="disabled">
      <transition name="fade-from-right">
        <div v-if="computedIcon">
          <icon-bounding-box :bounding-box="iconSize" :icon="computedIcon"/>
        </div>
      </transition>

      <slot v-if="!executing" name="prefix"/>

      <template v-if="executing">
        <bf-spinner size="5" color="white"/>
      </template>
      <template v-if="!executing || (executing && !executingLabel)">
        <slot/>
      </template>
      <template v-else>
        {{ executingLabel }}...
      </template>

      <slot name="suffix"/>
      <i v-if="notificationDot" class="w-[10px] h-[10px] absolute -right-[1px] top-[1px] bg-red-900 rounded-full"/>
    </button>
  </div>
</template>

<script setup lang="ts">
import type {PropType} from 'vue';
import type {ButtonColor, ButtonComponent, ButtonType} from '~/components/global/utils/buttonUtils';
import {iconProp, useIconProp} from '~/components/global/utils/iconPropUtil';
import type {InputSize} from '~/components/global/utils/InputUtils';

const props = defineProps({
  ...iconProp,

  label: {
    type: String,
    default: ''
  },
  tabindex: {
    type: [Number, String],
    default: 0,
    required: false
  },
  color: {
    type: String as PropType<ButtonColor>,
    default: 'primary'
  },
  classString: {
    type: String,
    default: ''
  },
  type: {
    type: String as PropType<ButtonType>,
    default: 'submit'
  },
  size: {
    type: String as PropType<InputSize>,
    default: 'base'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  executing: {
    type: Boolean,
    default: false
  },
  executingLabel: {
    type: String,
    default: ''
  },
  square: {
    type: Boolean,
    default: false
  },
  notificationDot: {
    type: Boolean,
    required: false,
    default: false
  },
  selected: {
    type: Boolean,
    default: false
  }
});

// Refs
const {computedIcon} = useIconProp(props);
const buttonRef = ref<HTMLButtonElement | null>(null);

// Methods
const focus = () => {
  if (buttonRef.value) buttonRef.value.focus();
};

// Computed properties
const skeletonClass = computed(() => {
  return props.color.toLowerCase() === 'primary' ? 'bg-skeleton-green' : '';
});
const sizeClasses = computed(() => {
  let sizeClass = {
    'base': 'text-base h-[41px] font-medium px-6 py-3.5',
    'xs': 'text-sm h-[32px] font-medium px-3 py-3.5',
    'xxs': 'text-xs h-[24px] font-medium px-3'
  }[props.size];

  if (props.square) sizeClass += ' aspect-1 rounded-md';
  else sizeClass += ' rounded-full';

  return sizeClass;
});
const iconSize = computed<number>(() => {
  return {
    'base': 24,
    'xs': 16,
    'xxs': 12
  }[props.size]
})
const colorClasses = computed(() => {
  if (props.disabled)
    return 'bg-black-100 text-black-400 cursor-not-allowed';

  return {
    'primary': 'bg-button-primary-background hover:bg-button-primary-hover text-button-primary-label focus:outline-none focus-visible:ring-4 focus-visible:ring-button-primary-background',
    'error': 'bg-button-error-background hover:bg-button-error-hover text-button-error-label focus:outline-none focus-visible:ring-4 focus-visible:ring-button-error-background',
    'outline': 'bg-button-outline-background border border-button-outline-border hover:bg-button-outline-hover-background hover:border-button-outline-hover-border hover:text-button-outline-hover-label text-button-outline-label focus:outline-none focus-visible:ring-4 focus-visible:ring-button-outline-border',
    'ghost': 'bg-button-outline-background border border-button-outline-border hover:bg-button-outline-hover-background hover:border-button-outline-hover-border hover:text-button-outline-hover-label text-button-outline-label focus:outline-none focus-visible:ring-4 focus-visible:ring-button-outline-border',
    'link': 'bg-button-link-background text-button-link-label hover:text-button-link-hover hover:underline focus:outline-none focus-visible:underline'
  }[props.color];
});

const selectedClasses = computed(() => {
  if (props.color === 'outline' && props.selected)
    return '!bg-button-outline-selected-background !text-button-outline-selected-label !border-button-outline-selected-border';

  return ''; // Return an empty string if the color is not outline or the selected prop is false
});

const executingClass = computed(() => {
  return props.executing ? 'pointer-events-none' : '';
});

defineExpose<ButtonComponent>({
  $el: buttonRef.value,
  focus
});
</script>